/**
 * AdminUsersPanel — Manage platform admin accounts (create / edit / delete).
 *
 * Extracted from `pages/AdminDashboard.jsx` (iter 438k Phase 1) so the
 * SuperAdmin portal at `/superadmin` can consume it directly. Behaviour is
 * unchanged from the previous inline version.
 */
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import { useAuthStore } from '@/lib/store';
import { getErrorMessage } from '@/lib/api';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { UserPlus, MoreVertical, Settings, Trash2, Loader2 } from 'lucide-react';
import { FinanceScopeNote } from '@/components/bank/FinanceScopeNote';

const AdminUsersPanel = () => {
  const queryClient = useQueryClient();
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const currentUser = useAuthStore((s) => s.user);
  const [editingAdmin, setEditingAdmin] = useState(null);
  // Nobody may change their own role or status — the backend refuses it, so
  // the controls are locked rather than failing on save.
  const isSelfEdit = !!editingAdmin && (
    editingAdmin.id === currentUser?.id || editingAdmin.email === currentUser?.email);
  const [deleteAdminId, setDeleteAdminId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    is_active: true,
  });

  const { data: admins, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch admins');
      return res.json();
    },
  });

  const handleCreateAdmin = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, email and password are required');
      return;
    }
    setIsSubmitting(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/admins`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success('Admin account created successfully');
        setShowCreateAdmin(false);
        setForm({ name: '', email: '', password: '', role: 'admin', is_active: true });
        queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      } else {
        const err = await res.json();
        toast.error(getErrorMessage(err, 'Failed to create admin'));
      }
    } catch (e) {
      toast.error('Failed to create admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAdmin = async () => {
    if (!editingAdmin) return;
    setIsSubmitting(true);
    try {
      const token = getToken();
      const updateData = { ...form };
      if (!updateData.password) delete updateData.password;
      const res = await fetch(`${API_URL}/api/admin/admins/${editingAdmin.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });
      if (res.ok) {
        toast.success('Admin account updated');
        setEditingAdmin(null);
        setForm({ name: '', email: '', password: '', role: 'admin', is_active: true });
        queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      } else {
        const err = await res.json();
        toast.error(getErrorMessage(err, 'Failed to update admin'));
      }
    } catch (e) {
      toast.error('Failed to update admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!deleteAdminId) return;
    setIsSubmitting(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/admins/${deleteAdminId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Admin account deleted');
        setDeleteAdminId(null);
        queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      } else {
        const err = await res.json();
        toast.error(getErrorMessage(err, 'Failed to delete admin'));
      }
    } catch (e) {
      toast.error('Failed to delete admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (admin) => {
    setEditingAdmin(admin);
    setForm({
      name: admin.name || '',
      email: admin.email || '',
      password: '',
      role: admin.role || 'admin',
      is_active: admin.is_active !== false,
    });
  };

  return (
    <div className="space-y-4" data-testid="admin-users-panel">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Admin Users</h2>
        <Button onClick={() => setShowCreateAdmin(true)} data-testid="add-admin-btn">
          <UserPlus className="h-4 w-4 mr-2" />
          Add Admin
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {admins?.map((admin) => (
                <div key={admin.id} className="p-4 flex items-center justify-between" data-testid={`admin-row-${admin.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-lg font-semibold text-slate-600">
                        {admin.name?.[0]?.toUpperCase() || admin.email?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium">{admin.name}</div>
                      <div className="text-sm text-slate-500">{admin.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={admin.role === 'super_admin' ? 'destructive' : 'secondary'}>
                      {admin.role?.replace('_', ' ')}
                    </Badge>
                    {admin.is_active === false && (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`admin-menu-${admin.id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(admin)}>
                          <Settings className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => setDeleteAdminId(admin.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Admin Dialog */}
      <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Admin Account</DialogTitle>
            <DialogDescription>Add a new administrator to the system</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="admin@example.com"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Enter password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="event_admin">Event Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAdmin(false)}>Cancel</Button>
            <Button onClick={handleCreateAdmin} disabled={isSubmitting} data-testid="create-admin-submit">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog open={!!editingAdmin} onOpenChange={() => setEditingAdmin(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Admin Account</DialogTitle>
            <DialogDescription>Update administrator details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>New Password (leave empty to keep current)</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Enter new password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} disabled={isSelfEdit}
                      onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="edit-admin-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="event_admin">Event Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
              {isSelfEdit && (
                <p className="text-xs text-slate-500 mt-1" data-testid="self-edit-role-note">
                  You cannot change your own role or account status — ask another super admin
                </p>
              )}
            </div>
            {editingAdmin?.id && <FinanceScopeNote adminId={editingAdmin.id} />}
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_active"
                checked={form.is_active}
                disabled={isSelfEdit}
                onCheckedChange={(c) => setForm({ ...form, is_active: c })}
              />
              <Label htmlFor="is_active">Active account</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAdmin(null)}>Cancel</Button>
            <Button onClick={handleUpdateAdmin} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteAdminId} onOpenChange={() => setDeleteAdminId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Admin Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The admin will lose access to the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAdmin}
              className="bg-red-600 hover:bg-red-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete Admin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUsersPanel;
